import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Heart } from "lucide-react";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.7 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.7 6.1 29.1 4 24 4 16.3 4 9.7 8.6 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5 0 9.6-1.9 13-5l-6-5.1c-1.9 1.4-4.3 2.2-7 2.2-5.3 0-9.7-3.4-11.3-8l-6.6 5C9.6 39.4 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.7 2-2 3.7-3.7 4.9l6 5.1c-.4.4 6.4-4.6 6.4-13.5 0-1.3-.1-2.4-.4-4.5z" />
  </svg>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleOAuth() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [search] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}.`);
      const next = search.get("next");
      navigate(next === "admin" || data.user.role === "admin" ? "/admin/import" : "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-page grid gap-12 py-16 lg:grid-cols-[1fr_1fr] lg:items-center" data-testid="login-page">
      <div className="hidden lg:block">
        <span className="chip">Welcome back</span>
        <h1 className="mt-4 font-display text-5xl font-bold leading-tight text-primary">
          Care that knows <span className="italic text-accent">your</span> story.
        </h1>
        <p className="mt-4 max-w-md text-foreground/70">
          Sign in to manage your listing, publish blog posts, and connect with your community.
        </p>
      </div>

      <div className="card-surface mx-auto w-full max-w-md p-8 sm:p-10">
        <div className="mb-8 flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-md bg-primary text-primary-foreground">
            <Heart className="h-5 w-5" strokeWidth={2.4} />
          </span>
          <span className="font-display text-2xl font-bold text-primary">Sign in</span>
        </div>

        <button
          type="button"
          onClick={startGoogleOAuth}
          className="flex w-full items-center justify-center gap-2.5 rounded-md border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground hover:border-accent hover:bg-secondary"
          data-testid="google-login-button"
        >
          <GoogleIcon /> Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or with email <span className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent"
              data-testid="login-password"
            />
          </div>
          <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="login-submit">
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          New here?{" "}
          <Link to="/signup" className="font-semibold text-primary hover:text-accent" data-testid="link-signup">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
